import React from 'react';
import { corPiscanteIntervaloAtiva } from '../fase/mapaFaseUtils';

function InfoRow({ label, value, highlight }) {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div className="mapa-fases-info-row">
      <span className="mapa-fases-info-row-label">{label}</span>
      <span className={`mapa-fases-info-row-value ${highlight ? 'is-highlight' : ''}`}>{value}</span>
    </div>
  );
}

const IMPACTO_LABEL = {
  via_fechada: 'Via fechada (Waze)',
  alto: 'Congestionamento Waze — nível alto',
  moderado: 'Congestionamento Waze — moderado',
  leve: 'Congestionamento Waze — leve',
  alerta: 'Alerta Waze próximo',
};

function cruzamentoComViaFechada(impactoCruzamento) {
  if (!impactoCruzamento) return false;
  if (impactoCruzamento.tem_via_fechada || impactoCruzamento.impacto === 'via_fechada') return true;
  return (impactoCruzamento.tipos_alerta || []).some(t => /fechad/i.test(String(t)));
}

export function MapaWazeInfoAlerta({ alerta, formatarData }) {
  return (
    <div className="mapa-fases-info-window mapa-waze-info-window">
      <div className="mapa-fases-info-header">
        <div>
          <strong>{alerta.tipo_label}</strong>
          <span className="mapa-fases-info-status mapa-waze-info-badge">Waze</span>
        </div>
      </div>
      <div className="mapa-fases-info-body">
        <InfoRow label="Detalhe" value={alerta.subtype_label || alerta.subtype} highlight />
        <InfoRow label="Via" value={alerta.rua || alerta.cidade} />
        {alerta.descricao ? <InfoRow label="Descrição" value={alerta.descricao} /> : null}
        <InfoRow label="Confiabilidade" value={alerta.confiabilidade != null ? `${alerta.confiabilidade}/10` : null} />
        <InfoRow label="Reportado" value={alerta.reportado_em ? formatarData(alerta.reportado_em) : null} />
      </div>
    </div>
  );
}

function ultimaLeituraWazeLabel(jam, feedLidoEm, formatarData) {
  if (jam.atualizado_waze_em && jam.atualizado_waze_em !== jam.reportado_em) {
    return formatarData(jam.atualizado_waze_em);
  }
  if (feedLidoEm) return formatarData(feedLidoEm);
  return null;
}

export function MapaWazeInfoJam({ jam, feedLidoEm, formatarData }) {
  const ultimaLeitura = ultimaLeituraWazeLabel(jam, feedLidoEm, formatarData);
  const labelUltimaLeitura =
    jam.atualizado_waze_em && jam.atualizado_waze_em !== jam.reportado_em ? 'Atualizado no Waze' : 'Última leitura';

  return (
    <div className="mapa-fases-info-window mapa-waze-info-window">
      <div className="mapa-fases-info-header">
        <div>
          <strong>Congestionamento</strong>
          <span className="mapa-fases-info-status mapa-waze-info-badge">Intensidade {jam.level}/5</span>
        </div>
      </div>
      <div className="mapa-fases-info-body">
        <InfoRow label="Via" value={jam.rua} />
        <InfoRow label="Nível" value={jam.level_label || null} />
        <InfoRow
          label="Velocidade"
          value={jam.velocidade_kmh != null ? `~${Math.round(jam.velocidade_kmh)} km/h` : null}
        />
        <InfoRow
          label="Trecho"
          value={jam.comprimento_m != null && jam.comprimento_m > 0 ? `~${Math.round(jam.comprimento_m)} m` : null}
        />
        <InfoRow
          label="Atraso"
          value={jam.atraso_s != null && jam.atraso_s > 0 ? `~${Math.round(jam.atraso_s)} s` : null}
          highlight
        />
        <InfoRow label="Reportado" value={jam.reportado_em ? formatarData(jam.reportado_em) : null} />
        <InfoRow label={labelUltimaLeitura} value={ultimaLeitura} />
      </div>
    </div>
  );
}

export function MapaWazeInfoFase({ fase, impactoCruzamento }) {
  const statusConexao = parseInt(fase.status_conexao, 10) <= 1 ? 'Conectado' : 'Sem conexão';
  const statusClass = parseInt(fase.status_conexao, 10) <= 1 ? 'is-online' : 'is-offline';
  const planoAtual =
    fase.plano_atu || (fase.plano_id !== undefined && fase.plano_id !== null ? `${fase.plano_id}` : '');
  const proximoPlano = fase.plano_pro || fase.hora_proximo_plano || '';
  const piscante = corPiscanteIntervaloAtiva(fase);
  const viaFechada = cruzamentoComViaFechada(impactoCruzamento);

  return (
    <div className="mapa-fases-info-window mapa-waze-info-window">
      <div className="mapa-fases-info-header">
        <div>
          <strong>Controlador {fase.numero_controlador}</strong>
          <span className={`mapa-fases-info-status ${statusClass}`}>{statusConexao}</span>
        </div>
      </div>
      <div className="mapa-fases-info-body">
        <InfoRow label="Endereço" value={fase.controlador_endereco || fase.endereco} />
        <InfoRow label="Anel" value={fase.anel} />
        <InfoRow label="Fase" value={fase.fase_id} />
        {piscante ? <InfoRow label="Estado" value={`${piscante} piscante`} highlight /> : null}
        {planoAtual ? <InfoRow label="Plano atual" value={planoAtual} highlight /> : null}
        {proximoPlano ? <InfoRow label="Próximo plano" value={proximoPlano} /> : null}
        {viaFechada ? (
          <InfoRow label="Waze" value={IMPACTO_LABEL.via_fechada} highlight />
        ) : impactoCruzamento && impactoCruzamento.impacto ? (
          <InfoRow
            label="Waze"
            value={IMPACTO_LABEL[impactoCruzamento.impacto] || impactoCruzamento.impacto}
            highlight
          />
        ) : (
          <InfoRow label="Waze" value="Sem impacto no raio do cruzamento" />
        )}
        {!viaFechada && impactoCruzamento && impactoCruzamento.jam_nivel_max ? (
          <InfoRow
            label="Fila (Waze)"
            value={
              impactoCruzamento.jam_distancia_m != null && impactoCruzamento.jam_distancia_m > 0
                ? `Intensidade ${impactoCruzamento.jam_nivel_max}/5 (trecho a ~${impactoCruzamento.jam_distancia_m} m)`
                : `Intensidade ${impactoCruzamento.jam_nivel_max}/5`
            }
          />
        ) : null}
      </div>
    </div>
  );
}
